import { Link } from "@tanstack/react-router";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useSafetyReviewQuery } from "../lib/portal-queries";

export const EXECUTIVE_PREVIEW_DISCLOSURE =
  "All warranty, cost, claims, forecast, SPC, and narrative values in this executive view currently come from local prototype projections. They are synthetic demonstration data, not governed customer facts.";

export const REGULATORY_SYNTHETIC_DISCLOSURE =
  "Governed synthetic safety scenarios are available through the tenant API. Counts represent demonstration evidence and review scenarios—not real recalls, filings, VINs, remedies, or deadlines.";

export const REGULATORY_AUTHORITY_DISCLOSURE =
  "Broadview supports evidence review and escalation. The customer retains regulatory and field-action authority.";

export function ExecutivePreviewNotice() {
  return (
    <section className="rounded-xl border border-warning/40 bg-warning/10 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <div>
          <h2 className="text-sm font-semibold">Demonstration-only executive preview</h2>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {EXECUTIVE_PREVIEW_DISCLOSURE}
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold">
            <span className="rounded-full bg-warning/15 px-2 py-1 text-warning">SYNTHETIC</span>
            <span className="rounded-full bg-warning/15 px-2 py-1 text-warning">
              DEMONSTRATION_ONLY
            </span>
            <span className="rounded-full bg-destructive/10 px-2 py-1 text-destructive">
              Production use prohibited
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function RegulatorySyntheticPanel() {
  const query = useSafetyReviewQuery();
  const count = (category: "field_actions" | "regulatory_evidence" | "campaign_completion") =>
    query.data?.widgets.find((widget) => widget.payload.category === category)?.payload.items
      .length ?? 0;
  const categories = [
    { label: "Field actions", value: count("field_actions"), suffix: "scenarios" },
    { label: "Regulatory evidence", value: count("regulatory_evidence"), suffix: "packages" },
    { label: "Affected populations", value: count("campaign_completion"), suffix: "scenarios" },
    { label: "Campaign completion", value: count("campaign_completion"), suffix: "scenarios" },
    { label: "Filing timing", value: count("regulatory_evidence"), suffix: "scenarios" },
  ] as const;

  return (
    <section
      className="rounded-xl border border-border bg-card p-5"
      data-testid="executive-safety-summary"
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">Safety & regulatory data</h2>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {REGULATORY_SYNTHETIC_DISCLOSURE}
          </p>
        </div>
        <Link
          to="/recall-safety"
          className="inline-flex items-center rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
        >
          Open governed safety review
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-5">
        {categories.map((category) => (
          <div
            key={category.label}
            className="rounded-lg border border-border bg-background/60 p-3"
          >
            <div className="text-[11px] text-muted-foreground">{category.label}</div>
            <div className="mt-1 text-sm font-semibold">
              {query.isPending ? "Loading…" : query.isError ? "API error" : category.value}
            </div>
            {!query.isPending && !query.isError && (
              <>
                <div className="text-[10px] text-muted-foreground">{category.suffix}</div>
                <div className="mt-1 text-[10px] font-semibold text-warning">SYNTHETIC</div>
              </>
            )}
          </div>
        ))}
      </div>

      <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
        {REGULATORY_AUTHORITY_DISCLOSURE}
      </p>
    </section>
  );
}
